import { useEffect, useState } from 'react'
import '../styles/RegionPopup.css'
import { getCountryFlag } from '../utils/flagCache'
import { getSeason } from '../utils/season'

const RegionPopup = ({ region, onClose, onCenterMap, onShareLocation, stats, weather, currentMonth }) => {
  const [flagUrl, setFlagUrl] = useState(null)

  useEffect(() => {
    getCountryFlag(region.country_id, setFlagUrl)
  }, [region.country_id])

  const formatPopulation = (pop) => {
    if (!pop || pop === 0) return 'Non renseigné'
    return pop.toLocaleString('fr-FR')
  }

  const formatArea = (areaKm2) => {
    if (!areaKm2 || areaKm2 === 0) return 'Non renseigné'
    if (areaKm2 >= 1000) {
      return `${areaKm2.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} km²`
    }
    return `${areaKm2.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} km²`
  }

  const fmt = (n) => (n ?? 0).toLocaleString('fr-FR')

  // Owned-country data: only present for the logged-in player's own regions.
  // Build a mini table: rows = type (+ specialisation), columns = levels present.
  const buildStructTable = (detail) => {
    const rows = new Map() // label -> { byLevel: {lvl: n}, total }
    const levels = new Set()
    ;(detail || []).forEach((d) => {
      if (!d.count) return
      levels.add(Number(d.level))
      const label = `${d.type}${d.specialisation && d.specialisation !== 'NA' ? ' ' + d.specialisation : ''}`
      const row = rows.get(label) || { byLevel: {}, total: 0 }
      row.byLevel[d.level] = (row.byLevel[d.level] || 0) + d.count
      row.total += d.count
      rows.set(label, row)
    })
    return {
      levels: [...levels].sort((a, b) => a - b),
      rows: [...rows.entries()]
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([label, r]) => ({ label, ...r })),
    }
  }

  const structTable = stats ? buildStructTable(stats.structures_detail) : { levels: [], rows: [] }
  const infraEntries = stats ? Object.entries(stats.infrastructure || {}) : []

  const getHemisphereText = (hemisphere) => {
    if (hemisphere === 'north') return 'Hémisphère Nord'
    if (hemisphere === 'south') return 'Hémisphère Sud'
    if (hemisphere === 'equator') return 'Équateur'
    return 'Non renseigné'
  }

  return (
    <div className="region-popup-overlay" onClick={onClose}>
      <div className="region-popup" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose} aria-label="Fermer">
          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="1" y1="1" x2="13" y2="13" />
            <line x1="13" y1="1" x2="1" y2="13" />
          </svg>
        </button>

        <div className="region-header">
          <h2>{region.name}</h2>
          <p className="region-area">{region.geographical_area || region.country_name}</p>
        </div>

        <div className="region-details">
          <div className="detail-row">
            <span className="detail-label">Continent</span>
            <span className="detail-value">{region.continent || 'Non renseigné'}</span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Population</span>
            <span className="detail-value">{formatPopulation(region.population)}</span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Surface</span>
            <span className="detail-value">{formatArea(region.area_km2)}</span>
          </div>

          {region.area_km2 > 0 && region.population > 0 && (
            <div className="detail-row">
              <span className="detail-label">Densité</span>
              <span className="detail-value">
                {(region.population / region.area_km2).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} hab/km²
              </span>
            </div>
          )}

          {(region.altitude ?? region.elevation ?? region.avg_altitude) != null && (
            <div className="detail-row">
              <span className="detail-label">Altitude</span>
              <span className="detail-value">
                {fmt(region.altitude ?? region.elevation ?? region.avg_altitude)} m
              </span>
            </div>
          )}

          <div className="detail-row">
            <span className="detail-label">Hémisphère</span>
            <span className="detail-value">{getHemisphereText(region.hemisphere)}</span>
          </div>

          {currentMonth && (
            <div className="detail-row">
              <span className="detail-label">Saison</span>
              <span className="detail-value">{getSeason(region.hemisphere, currentMonth)}</span>
            </div>
          )}

          {region.country_name && (
            <div className="detail-row">
              <span className="detail-label">Pays</span>
              <span className="detail-value">
                {flagUrl && <img src={flagUrl} alt="" className="country-flag-icon-sm" />}
                {region.country_name}
              </span>
            </div>
          )}
          
          {region.country_name && region.is_played !== undefined && (
            <div className="detail-row">
              <span className="detail-label">Statut</span>
              <span className="detail-value">
                {region.is_played ? 'Pays joué' : 'Pays non joué'}
                {region.months_since_activity !== null && region.months_since_activity !== undefined && (
                  <span style={{ marginLeft: '8px', fontSize: '0.9em' }}>
                    ({region.months_since_activity > 5 || region.months_since_activity === null
                      ? '⚠️ Inactif >5 mois'
                      : `Actif (${region.months_since_activity} mois)`})
                  </span>
                )}
              </span>
            </div>
          )}

          {region.climate && (
            <div className="detail-row">
              <span className="detail-label">Climat</span>
              <span className="detail-value">{region.climate}</span>
            </div>
          )}

          {weather && (
            <div className="detail-row">
              <span className="detail-label">Météo</span>
              <span className="detail-value">
                {weather.weather_type}
                {weather.temperature !== undefined && ` — ${Math.round(weather.temperature)}°C`}
                {weather.wind_speed !== undefined && `, vent ${Math.round(weather.wind_speed)} km/h`}
              </span>
            </div>
          )}

          <div className="detail-row">
            <span className="detail-label">ID Région</span>
            <span className="detail-value">#{region.region_id}</span>
          </div>
        </div>

        {stats && (
          <div className="region-owned-stats">
            <h3 className="owned-stats-title">🏙️ Mes données régionales</h3>

            <div className="owned-stats-group">
              <h4>Logement</h4>
              <div className="detail-row">
                <span className="detail-label">Capacité</span>
                <span className="detail-value">{fmt(stats.housing?.capacity)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Couverture</span>
                <span className="detail-value">{fmt(stats.housing?.coverage_pct)} %</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Confort moyen</span>
                <span className="detail-value">{fmt(stats.housing?.average_comfort)}</span>
              </div>
            </div>

            <div className="owned-stats-group">
              <h4>Électricité</h4>
              <div className="detail-row">
                <span className="detail-label">Production</span>
                <span className="detail-value">{fmt(stats.electricity?.production_mwh)} MWh</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Consommation</span>
                <span className="detail-value">{fmt(stats.electricity?.consumption_mwh)} MWh</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Couverture conso.</span>
                <span className="detail-value">{fmt(stats.electricity?.meeting_pct)} %</span>
              </div>
            </div>

            <div className="owned-stats-group">
              <h4>Structures</h4>
              {structTable.rows.length > 0 ? (
                <table className="owned-stats-table">
                  <thead>
                    <tr>
                      <th>Structure</th>
                      {structTable.levels.map((l) => (
                        <th key={l}>Nv.{l}</th>
                      ))}
                      <th>Σ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {structTable.rows.map((row) => (
                      <tr key={row.label}>
                        <td className="owned-stats-rowlabel">{row.label}</td>
                        {structTable.levels.map((l) => (
                          <td key={l}>{row.byLevel[l] ? fmt(row.byLevel[l]) : '·'}</td>
                        ))}
                        <td className="owned-stats-total">{fmt(row.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="detail-row">
                  <span className="detail-value owned-stats-empty">Aucune structure</span>
                </div>
              )}
            </div>

            <div className="owned-stats-group">
              <h4>Infrastructures</h4>
              {infraEntries.length > 0 ? (
                infraEntries.map(([type, km]) => (
                  <div className="detail-row" key={type}>
                    <span className="detail-label">{type}</span>
                    <span className="detail-value">{fmt(km)} km</span>
                  </div>
                ))
              ) : (
                <div className="detail-row">
                  <span className="detail-value owned-stats-empty">Aucune infrastructure</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="region-actions">
          <button 
            className="center-map-button"
            onClick={() => onCenterMap && onCenterMap(region)}
          >
            📍 Centrer sur la carte
          </button>
          
          <button 
            className="share-location-button"
            onClick={() => onShareLocation && onShareLocation(region)}
          >
            🔗 Copier le lien
          </button>
        </div>

        {region.country_name && (
          <div className="region-footer">
            <span
              className="country-badge"
              style={{ backgroundColor: region.country_color || '#999' }}
            >
              {flagUrl && <img src={flagUrl} alt="" className="country-flag-icon-sm" />}
              {region.country_name}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export default RegionPopup
